import {
  AcceptanceInspectionAttachment,
  AcceptanceInspectionItem,
} from '../../types/acceptanceInspection';
import { inventoryKindLabel } from '../../types/companyInventory';
import { groupInventoryByMaster } from '../../utils/inventoryGrouping';
import { PartVendor, partVendorLabel } from '../../types/partVendor';
import './InspectionFormSheet.css';

export interface AcceptanceSheetValues {
  /** 재고 품목 ID (등록 시 필수) */
  inventoryId: number | null;
  itemKind: string;
  /** 품명 스냅샷 */
  productName: string;
  authorName: string;
  reviewerName?: string;
  approverName?: string;
  specText: string;
  lotNo: string;
  material: string;
  incomingQty: string;
  inspectorName: string;
  vendorName: string;
  vendorContactName: string;
  vendorPhone: string;
  incomingDate: string;
  inspectionDate: string;
  overallJudgment: string;
  remark: string;
  items: AcceptanceInspectionItem[];
}

export interface InventoryOption {
  inventoryId: number;
  itemKind?: string | null;
  itemName: string;
  specText?: string | null;
}

interface Props {
  values: AcceptanceSheetValues;
  editable?: boolean;
  attachmentsEditable?: boolean;
  mediaAttachments?: AcceptanceInspectionAttachment[];
  remarkAttachments?: AcceptanceInspectionAttachment[];
  pendingMediaFiles?: File[];
  pendingRemarkFiles?: File[];
  mediaPreviewUrls?: Record<number, string>;
  pendingMediaPreviewUrls?: Record<number, string>;
  inventoryOptions?: InventoryOption[];
  onChange?: (patch: Partial<AcceptanceSheetValues>) => void;
  onItemChange?: (index: number, patch: Partial<AcceptanceInspectionItem>) => void;
  onAddItem?: () => void;
  onRemoveItem?: (index: number) => void;
  onPickMediaFiles?: (files: File[]) => void;
  onPickRemarkFiles?: (files: File[]) => void;
  onRemovePendingMediaFile?: (index: number) => void;
  onRemovePendingRemarkFile?: (index: number) => void;
  onDownloadAttachment?: (attachment: AcceptanceInspectionAttachment) => void;
  onDeleteAttachment?: (attachment: AcceptanceInspectionAttachment) => void;
  onOpenQuickRegister?: () => void;
  vendorOptions?: PartVendor[];
  onPickVendor?: (vendorId: number) => void;
  onOpenVendorQuickRegister?: () => void;
  /** Lot No. 입력 힌트 (직전 값 안내 등) */
  lotNoHint?: string;
}

function Field({
  label,
  editable,
  value,
  onChange,
  type = 'text',
  hint,
  placeholder,
  required,
}: {
  label: string;
  editable?: boolean;
  value: string;
  onChange?: (value: string) => void;
  type?: string;
  hint?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label>
      <span className="ifs-field-label">
        {label}
        {required ? <span className="ifs-field-required" aria-hidden> *</span> : null}
      </span>
      {editable ? (
        type === 'select-judgment' ? (
          <select value={value} onChange={(e) => onChange?.(e.target.value)} required={required}>
            <option value="">선택</option>
            <option value="적합">적합</option>
            <option value="부적합">부적합</option>
          </select>
        ) : (
          <input
            type={type}
            value={value}
            placeholder={placeholder}
            required={required}
            onChange={(e) => onChange?.(e.target.value)}
          />
        )
      ) : (
        <input value={value || ''} readOnly placeholder={placeholder || (value ? undefined : '-')} />
      )}
      {hint ? <span className="ifs-field-hint">{hint}</span> : null}
    </label>
  );
}

function optionLabel(opt: InventoryOption) {
  const name = opt.itemName?.trim() || '품명 없음';
  const spec = opt.specText?.trim() || '기본';
  return `${name} / ${spec}`;
}

function groupOptions(options: InventoryOption[]) {
  return groupInventoryByMaster(
    options.map((opt) => ({
      inventoryId: opt.inventoryId,
      itemKind: opt.itemKind || 'PART',
      itemName: opt.itemName,
      specText: opt.specText ?? '',
    }))
  ).map((group) => ({
    kind: String(group.masterId ?? `${group.itemKind}-${group.itemName}`),
    label: `[${inventoryKindLabel(group.itemKind)}] ${group.itemName}`,
    items: group.specs.map((spec) => ({
      inventoryId: spec.inventoryId,
      itemKind: spec.itemKind,
      itemName: spec.itemName,
      specText: spec.specText,
    })),
  }));
}

function FileCards({
  attachments,
  pendingFiles,
  previewUrls,
  pendingPreviewUrls,
  editable,
  emptyText,
  onDownloadAttachment,
  onDeleteAttachment,
  onRemovePending,
}: {
  attachments: AcceptanceInspectionAttachment[];
  pendingFiles: File[];
  previewUrls?: Record<number, string>;
  pendingPreviewUrls?: Record<number, string>;
  editable?: boolean;
  emptyText: string;
  onDownloadAttachment?: (attachment: AcceptanceInspectionAttachment) => void;
  onDeleteAttachment?: (attachment: AcceptanceInspectionAttachment) => void;
  onRemovePending?: (index: number) => void;
}) {
  if (attachments.length === 0 && pendingFiles.length === 0) {
    return <p style={{ margin: 0, fontSize: 12, color: '#98a2b3' }}>{emptyText}</p>;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start' }}>
      {attachments.map((file) => {
        const isImage = (file.contentType || '').startsWith('image/');
        const previewUrl = previewUrls?.[file.attachmentId];
        return (
          <div
            key={file.attachmentId}
            style={{
              width: isImage ? 140 : 180,
              border: '1px solid #d0d5dd',
              background: '#fff',
              padding: 6,
              fontSize: 12,
            }}
          >
            {isImage && previewUrl ? (
              <button
                type="button"
                onClick={() => onDownloadAttachment?.(file)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                <img
                  src={previewUrl}
                  alt={file.fileName}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 100,
                    objectFit: 'cover',
                    background: '#eef1f5',
                  }}
                />
              </button>
            ) : (
              <button
                type="button"
                className="is-ghost"
                style={{ padding: 0, border: 'none', color: '#1a5fb4', textAlign: 'left' }}
                onClick={() => onDownloadAttachment?.(file)}
              >
                {file.fileName}
              </button>
            )}
            <div style={{ marginTop: 4, wordBreak: 'break-all', color: '#445062' }}>{file.fileName}</div>
            {editable && onDeleteAttachment ? (
              <button
                type="button"
                className="is-danger"
                style={{ marginTop: 4 }}
                onClick={() => onDeleteAttachment(file)}
              >
                삭제
              </button>
            ) : null}
          </div>
        );
      })}

      {editable
        ? pendingFiles.map((file, index) => {
            const isImage = file.type.startsWith('image/');
            const localUrl = pendingPreviewUrls?.[index];
            return (
              <div
                key={`${file.name}-${index}`}
                style={{
                  width: isImage ? 140 : 180,
                  border: '1px dashed #98a2b3',
                  background: '#fff',
                  padding: 6,
                  fontSize: 12,
                }}
              >
                {isImage && localUrl ? (
                  <img
                    src={localUrl}
                    alt={file.name}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 100,
                      objectFit: 'cover',
                      background: '#eef1f5',
                    }}
                  />
                ) : null}
                <div style={{ marginTop: 4, wordBreak: 'break-all', color: '#445062' }}>
                  {file.name}
                  <span style={{ color: '#98a2b3' }}> (저장 시 업로드)</span>
                </div>
                <button
                  type="button"
                  className="is-ghost"
                  style={{ marginTop: 4 }}
                  onClick={() => onRemovePending?.(index)}
                >
                  취소
                </button>
              </div>
            );
          })
        : null}
    </div>
  );
}

function AcceptanceInspectionSheet({
  values,
  editable = false,
  attachmentsEditable = editable,
  mediaAttachments = [],
  remarkAttachments = [],
  pendingMediaFiles = [],
  pendingRemarkFiles = [],
  mediaPreviewUrls,
  pendingMediaPreviewUrls,
  inventoryOptions = [],
  onChange,
  onItemChange,
  onAddItem,
  onRemoveItem,
  onPickMediaFiles,
  onPickRemarkFiles,
  onRemovePendingMediaFile,
  onRemovePendingRemarkFile,
  onDownloadAttachment,
  onDeleteAttachment,
  onOpenQuickRegister,
  vendorOptions = [],
  onPickVendor,
  onOpenVendorQuickRegister,
  lotNoHint,
}: Props) {
  const patch = (key: keyof AcceptanceSheetValues, value: string) => {
    onChange?.({ [key]: value } as Partial<AcceptanceSheetValues>);
  };

  const selectedLabel = values.productName
    ? `${values.productName}${values.specText ? ` · ${values.specText}` : ''}`
    : '';
  const grouped = groupOptions(inventoryOptions);

  const pickInventory = (rawId: string) => {
    if (!rawId) {
      onChange?.({
        inventoryId: null,
        itemKind: '',
        productName: '',
        specText: '',
      });
      return;
    }
    const id = Number(rawId);
    const opt = inventoryOptions.find((row) => row.inventoryId === id);
    if (!opt) return;
    onChange?.({
      inventoryId: opt.inventoryId,
      itemKind: opt.itemKind ?? '',
      productName: opt.itemName,
      specText: opt.specText ?? '',
    });
  };

  return (
    <div className="ifs-sheet">
      <div className="ifs-sheet__top">
        <div>
          <h4 className="ifs-sheet__title">인수검사성적서</h4>
        </div>
        <div className="ifs-sheet__stamps">
          <div>
            <strong>작성</strong>
            <span>{values.authorName || '-'}</span>
          </div>
          <div>
            <strong>검토</strong>
            <span
              style={{
                color: values.reviewerName ? '#1f2a37' : '#98a2b3',
                fontWeight: values.reviewerName ? 600 : 400,
              }}
            >
              {values.reviewerName || '-'}
            </span>
          </div>
          <div>
            <strong>승인</strong>
            <span
              style={{
                color: values.approverName ? '#1f2a37' : '#98a2b3',
                fontWeight: values.approverName ? 600 : 400,
              }}
            >
              {values.approverName || '-'}
            </span>
          </div>
        </div>
      </div>

      <div className="ifs-sheet__field-group">
        <h5 className="ifs-sheet__field-group-title">품목 정보</h5>
        <div className="ifs-sheet__fields is-cols-4">
          <label className="ifs-sheet__item-pick">
            품목 (재고관리)
            {editable ? (
              <div className="ifs-sheet__item-row">
                <select
                  value={values.inventoryId ?? ''}
                  onChange={(e) => pickInventory(e.target.value)}
                >
                  <option value="">품목을 선택하세요</option>
                  {grouped.map((group) => (
                    <optgroup key={group.kind} label={group.label}>
                      {group.items.map((opt) => (
                        <option key={opt.inventoryId} value={opt.inventoryId}>
                          {optionLabel(opt)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {onOpenQuickRegister ? (
                  <button type="button" className="is-ghost" onClick={onOpenQuickRegister}>
                    빠른 등록
                  </button>
                ) : null}
              </div>
            ) : (
              <input value={selectedLabel || '-'} readOnly />
            )}
          </label>
          <label>
            규격
            <input value={values.specText || (editable ? '' : '-')} readOnly />
          </label>
          <Field
            label="Lot No."
            editable={false}
            value={values.lotNo}
            placeholder={editable && !values.lotNo ? '저장 시 자동 발급' : undefined}
          />
          <Field
            label="재질"
            editable={editable}
            value={values.material}
            onChange={(v) => patch('material', v)}
          />
          <Field
            label="입고 수량"
            editable={editable}
            value={values.incomingQty}
            onChange={(v) => patch('incomingQty', v)}
            required={editable}
          />
        </div>
        {editable && !values.lotNo && lotNoHint ? (
          <p className="ifs-field-hint" style={{ marginTop: 8 }}>
            {lotNoHint}
          </p>
        ) : null}
      </div>

      <div className="ifs-sheet__field-group">
        <div className="ifs-sheet__toolbar">
          <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
            공급업체
          </h5>
          {editable ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value=""
                onChange={(e) => {
                  const id = Number(e.target.value);
                  if (id) onPickVendor?.(id);
                  e.target.value = '';
                }}
                style={{ minWidth: 180 }}
              >
                <option value="">등록 업체에서 선택</option>
                {vendorOptions.map((vendor) => (
                  <option key={vendor.vendorId} value={vendor.vendorId}>
                    {partVendorLabel(vendor)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="is-ghost"
                onClick={() => onOpenVendorQuickRegister?.()}
              >
                빠른 등록
              </button>
            </div>
          ) : null}
        </div>
        <div className="ifs-sheet__fields is-cols-3">
          <Field
            label="업체명"
            editable={editable}
            value={values.vendorName}
            onChange={(v) => patch('vendorName', v)}
          />
          <Field
            label="담당자"
            editable={editable}
            value={values.vendorContactName}
            onChange={(v) => patch('vendorContactName', v)}
          />
          <Field
            label="연락처"
            editable={editable}
            value={values.vendorPhone}
            onChange={(v) => patch('vendorPhone', v)}
          />
        </div>
      </div>

      <div className="ifs-sheet__field-group">
        <h5 className="ifs-sheet__field-group-title">검사 정보</h5>
        <div className="ifs-sheet__fields is-cols-3">
          <Field
            label="검사자"
            editable={editable}
            value={values.inspectorName}
            onChange={(v) => patch('inspectorName', v)}
          />
          <Field
            label="입고 일자"
            editable={editable}
            type="date"
            value={values.incomingDate}
            onChange={(v) => patch('incomingDate', v)}
          />
          <Field
            label="검사 일자"
            editable={editable}
            type="date"
            value={values.inspectionDate}
            onChange={(v) => patch('inspectionDate', v)}
          />
        </div>
      </div>

      <div className="ifs-sheet__section">
        <div className="ifs-sheet__toolbar">
          <strong>제품 사진 / 파일</strong>
          {attachmentsEditable ? (
            <label className="ifs-sheet__file-btn">
              사진·파일 추가
              <input
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length) onPickMediaFiles?.(files);
                  e.target.value = '';
                }}
              />
            </label>
          ) : null}
        </div>
        <p style={{ margin: '0 0 8px', fontSize: 12, color: '#667085' }}>
          성적서 중간에 넣을 제품 사진이나 관련 파일을 첨부하세요.
        </p>
        <FileCards
          attachments={mediaAttachments}
          pendingFiles={pendingMediaFiles}
          previewUrls={mediaPreviewUrls}
          pendingPreviewUrls={pendingMediaPreviewUrls}
          editable={attachmentsEditable}
          emptyText="첨부된 사진/파일이 없습니다."
          onDownloadAttachment={onDownloadAttachment}
          onDeleteAttachment={onDeleteAttachment}
          onRemovePending={onRemovePendingMediaFile}
        />
      </div>

      <section className="ifs-sheet__field-group ifs-req">
        <div className="ifs-req__head">
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              검사 항목
            </h5>
            <p className="ifs-req__hint">
              {values.items.length > 0
                ? `${values.items.length}개 항목`
                : editable
                  ? '검사항목을 추가해 주세요'
                  : '등록된 항목 없음'}
            </p>
          </div>
          {editable ? (
            <button type="button" className="ifs-req__add" onClick={() => onAddItem?.()}>
              + 행 추가
            </button>
          ) : null}
        </div>

        {values.items.length === 0 ? (
          <div className="ifs-req__empty">
            <strong>검사 항목이 없습니다</strong>
            <span>{editable ? '행 추가로 검사항목·기준·방법을 입력하세요.' : ''}</span>
          </div>
        ) : (
          <div className="ifs-req__list">
            {values.items.map((item, index) => {
              const judgment = (item.judgment ?? '').trim();
              const judgmentClass =
                judgment === '적합'
                  ? 'is-pass'
                  : judgment === '부적합'
                    ? 'is-fail'
                    : 'is-pending';
              return (
                <article key={item.itemId ?? `acc-item-${index}`} className="ifs-req__card">
                  <header className="ifs-req__card-top">
                    <span className="ifs-req__no">{index + 1}</span>
                    <div className="ifs-req__title-wrap">
                      {editable ? (
                        <textarea
                          className="ifs-req__title-input"
                          value={item.itemName ?? ''}
                          onChange={(e) => onItemChange?.(index, { itemName: e.target.value })}
                          placeholder="검사항목명"
                          rows={1}
                        />
                      ) : (
                        <strong className="ifs-req__title-text">{item.itemName || '-'}</strong>
                      )}
                    </div>
                    <div className={`ifs-req__judgment ${judgmentClass}`}>
                      {editable ? (
                        <select
                          value={item.judgment ?? ''}
                          onChange={(e) => onItemChange?.(index, { judgment: e.target.value })}
                        >
                          <option value="">판정</option>
                          <option value="적합">적합</option>
                          <option value="부적합">부적합</option>
                        </select>
                      ) : (
                        <span>{judgment || '-'}</span>
                      )}
                    </div>
                    {editable ? (
                      <button
                        type="button"
                        className="ifs-req__remove"
                        onClick={() => onRemoveItem?.(index)}
                        title="삭제"
                        aria-label="검사항목 삭제"
                      >
                        삭제
                      </button>
                    ) : null}
                  </header>

                  <div className="ifs-req__body is-cols-3">
                    <label className="ifs-req__field">
                      <span>검사설명</span>
                      {editable ? (
                        <textarea
                          value={item.descriptionText ?? ''}
                          onChange={(e) =>
                            onItemChange?.(index, { descriptionText: e.target.value })
                          }
                          placeholder="검사설명"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.descriptionText || '-'}</p>
                      )}
                    </label>
                    <label className="ifs-req__field">
                      <span>Spec</span>
                      {editable ? (
                        <textarea
                          value={item.standardText ?? ''}
                          onChange={(e) =>
                            onItemChange?.(index, { standardText: e.target.value })
                          }
                          placeholder="Spec / 합격 기준"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.standardText || '-'}</p>
                      )}
                    </label>
                    <label className="ifs-req__field">
                      <span>검사방법 및 조건</span>
                      {editable ? (
                        <textarea
                          value={item.methodText ?? ''}
                          onChange={(e) => onItemChange?.(index, { methodText: e.target.value })}
                          placeholder="검사 방법·조건"
                          rows={3}
                        />
                      ) : (
                        <p className="ifs-req__readonly">{item.methodText || '-'}</p>
                      )}
                    </label>
                  </div>

                  <div className="ifs-req__samples">
                    <span className="ifs-req__samples-label">시료</span>
                    <div className="ifs-req__sample-grid">
                      {(['sample1', 'sample2', 'sample3', 'sample4', 'sample5'] as const).map(
                        (key, sampleIndex) => (
                          <label key={key} className="ifs-req__sample">
                            <span>X{sampleIndex + 1}</span>
                            <input
                              value={item[key] ?? ''}
                              readOnly={!editable}
                              onChange={(e) =>
                                onItemChange?.(index, { [key]: e.target.value })
                              }
                              placeholder="-"
                            />
                          </label>
                        )
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <div
        className={`ifs-overall${
          values.overallJudgment === '적합'
            ? ' is-pass'
            : values.overallJudgment === '부적합'
              ? ' is-fail'
              : ''
        }`}
      >
        <div className="ifs-overall__head">
          <div>
            <h5 className="ifs-sheet__field-group-title" style={{ margin: 0 }}>
              종합판정
              {editable ? <span className="ifs-field-required" aria-hidden> *</span> : null}
            </h5>
            <p className="ifs-overall__hint">
              검사 항목을 모두 확인한 뒤 최종 판정하세요
            </p>
          </div>
          {!editable && values.overallJudgment ? (
            <span
              className={`ifs-overall__badge${
                values.overallJudgment === '적합'
                  ? ' is-pass'
                  : values.overallJudgment === '부적합'
                    ? ' is-fail'
                    : ''
              }`}
            >
              {values.overallJudgment}
            </span>
          ) : null}
        </div>
        {editable ? (
          <div className="ifs-overall__choices" role="group" aria-label="종합판정">
            <button
              type="button"
              className={`ifs-overall__choice is-pass${
                values.overallJudgment === '적합' ? ' is-selected' : ''
              }`}
              onClick={() => patch('overallJudgment', '적합')}
            >
              <strong>적합</strong>
              <span>합격 · 입고 가능</span>
            </button>
            <button
              type="button"
              className={`ifs-overall__choice is-fail${
                values.overallJudgment === '부적합' ? ' is-selected' : ''
              }`}
              onClick={() => patch('overallJudgment', '부적합')}
            >
              <strong>부적합</strong>
              <span>불합격 · NCR 검토</span>
            </button>
          </div>
        ) : !values.overallJudgment ? (
          <p className="ifs-overall__empty">판정 없음</p>
        ) : null}
      </div>

      <div className="ifs-sheet__remark">
        <div className="ifs-sheet__toolbar">
          <strong>비고</strong>
          {attachmentsEditable ? (
            <label className="ifs-sheet__file-btn is-soft">
              파일 첨부
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length) onPickRemarkFiles?.(files);
                  e.target.value = '';
                }}
              />
            </label>
          ) : null}
        </div>
        {editable ? (
          <textarea
            value={values.remark}
            onChange={(e) => patch('remark', e.target.value)}
            rows={3}
            placeholder="공인기관 성적서 대체 시 비고와 함께 첨부하세요."
            style={{ marginBottom: 8 }}
          />
        ) : (
          <p style={{ margin: '0 0 8px', fontSize: 13, whiteSpace: 'pre-wrap' }}>
            {values.remark || '-'}
          </p>
        )}
        <FileCards
          attachments={remarkAttachments}
          pendingFiles={pendingRemarkFiles}
          editable={attachmentsEditable}
          emptyText="공인기관 성적서 등 비고 첨부파일이 없습니다."
          onDownloadAttachment={onDownloadAttachment}
          onDeleteAttachment={onDeleteAttachment}
          onRemovePending={onRemovePendingRemarkFile}
        />
      </div>
    </div>
  );
}

export default AcceptanceInspectionSheet;
